import PropTypes from 'prop-types';
import React from 'react';
import { Prompt } from 'react-router-dom';
import _ from 'lodash';
import ProcessService from 'services/ProcessService';
import TabsService from 'services/TabsService';
import AceEditor from 'react-ace';
import ProcessConverter from 'components/page/process/ProcessConverter';
import 'brace/mode/json';
import 'brace/theme/github';
import 'brace/ext/searchbox';
import Flexbox from 'flexbox-react';
import Toolbar from 'components/core/toolbar/Toolbar';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';


/**
 * The form that renders the general process details
 */
export default class ProcessTextEditor extends React.Component {

    static propTypes = {
        process: PropTypes.object,
        hasWritePermissions: PropTypes.bool
    };

    constructor(props) {
        super(props);

        const debugging = false;
        if (debugging) {
            this.writeDebugLogs(props);
        }
        this.production = props.process.production; // save production flag so we can restore it later
        const oldFormat = ProcessConverter.newToOldProcess(_.cloneDeep(props.process));

        this.state = {
            isDirty: false,
            value: JSON.stringify(oldFormat, null, 2),
            theme: 'github',
            mode: 'json',
            ignoreBlock: null,
            enableBasicAutocompletion: false,
            enableLiveAutocompletion: false,
            fontSize: 14,
            showGutter: true,
            showPrintMargin: true,
            highlightActiveLine: true,
            enableSnippets: false,
            showLineNumbers: true,
            isOldFormat: true
        };
    }

    writeDebugLogs(props) {

        console.log('server process: ', props.process);
        let oldProcess = ProcessConverter.newToOldProcess(_.cloneDeep(props.process));
        console.log('converted old Process: ', oldProcess);
        let newProcess = ProcessConverter.oldToNewProcess(_.cloneDeep(oldProcess));
        console.log('converted new process: ', newProcess);
        let reconvertedOldProcess = ProcessConverter.newToOldProcess(_.cloneDeep(newProcess));
        let reconvertedNewProcess = ProcessConverter.oldToNewProcess(_.cloneDeep(reconvertedOldProcess));
        console.log('reconverted old process: ', reconvertedOldProcess);
        console.log('server equal to converted? ', _.isEqual(reconvertedNewProcess, newProcess));

        let diffFound = false;
        // conversion from new to old to new (ignoring 'ignore' on the first conversion)
        Object.keys(props.process).forEach(item => {
            if (!diffFound && item !== 'ignore' && !_.isEqual(props.process[item], newProcess[item])) {
                diffFound = true;
                console.log(item, ' is not equal after converting back and forth');
                console.log(props.process[item]);
                console.log(newProcess[item]);
            }
        });

        diffFound = false;
        // conversion from new to old to new to old to new
        Object.keys(reconvertedNewProcess).forEach(item => {
            if (!diffFound && !_.isEqual(reconvertedNewProcess[item], newProcess[item])) {
                diffFound = true;
                console.log(item, ' is not equal after converting back and forth');
                console.log(reconvertedNewProcess[item]);
                console.log(newProcess[item]);
            }
        });

        diffFound = false;
        // conversion from new to old to new to old
        Object.keys(reconvertedOldProcess).forEach(item => {
            if (!diffFound && !_.isEqual(reconvertedOldProcess[item], oldProcess[item])) {
                diffFound = true;
                console.log(item, ' is not equal after converting back and forth');
                console.log(reconvertedOldProcess[item]);
                console.log(oldProcess[item]);
            }
        });
    }

    isJsonValid() {
        // Checks if current json in the editor is valid
        let valid = true;
        try {
            JSON.parse(this.state.value);
        } catch (e) {
            valid = false;
        }
        return valid;
    }

    toggleFormat = () => {
        const currentValue = JSON.parse(this.state.value);
        if (this.state.ignoreBlock) {
            currentValue.ignore = this.state.ignoreBlock;
        }
        const convertedProcess = (ProcessConverter.toggleFormat(_.cloneDeep(currentValue), this.state.isOldFormat));
        const ignoreBlock = _.cloneDeep(convertedProcess.ignore);
        delete convertedProcess.ignore;
        this.setState({
            isOldFormat: !this.state.isOldFormat,
            ignoreBlock,
            value: JSON.stringify(convertedProcess, null, 2)
        });
    };

    applyChanges = () => {
        let currentValue = this.state.value;
        // If it's in the old format, we need to convert it back to the new format for use in the UI
        if (this.state.isOldFormat) {
            currentValue = JSON.stringify(ProcessConverter.oldToNewProcess(JSON.parse(this.state.value)));
        }
        const newProcess = JSON.parse(currentValue);

        newProcess.production = this.production; // copy back production value

        const processId = this.props.process.name;
        ProcessService.updateProcess(processId, newProcess);
        this.setState({
            isDirty: false
        });

        if (processId !== newProcess.name) {
            // If the name changed, then we need to refresh the tab
            TabsService.renameTab(processId, newProcess.name, `/process/${newProcess.name}/text`);
        }
    };

    onChange = (newValue) => {
        this.setState({
            value: newValue,
            isDirty: true
        });
    };

    render() {
        /**
         *
         height at 100% isn't working
         <div className="panel-body" style={ { padding: '0px' } }>
         */

        return (
            <Flexbox className="reverse-blue-header panel panel-default process-text-editor" flexDirection="column" flexGrow={1}>
                <div className="panel-heading">
                    Process Text Editor
                    <i className="fa fa-exchange" aria-hidden="true" style={{ color: 'white' }} onClick={this.toggleFormat}/>
                    <Toolbar>
                        <ToolbarItem title="Apply Changes" textOnly clickHandler={this.applyChanges}
                            enabled={this.props.hasWritePermissions && this.state.isDirty && this.isJsonValid()}
                        />
                    </Toolbar>
                </div>

                {/* Add a react router prompt if the user tries to navigate and there are unapplied changes*/}
                {/* TODO: for a nicer look, override the default prompt ui: https://kamranicus.com/posts/2018-07-26-react-router-custom-transition-ui*/}
                <Prompt
                    when={this.state.isDirty}
                    message="You have unapplied changes.  Are you sure you want to leave the text editor?"
                />
                <AceEditor
                    mode={this.state.mode}
                    theme={this.state.theme}
                    editorProps={{ $blockScrolling: 'Infinity' }}
                    width="100%"
                    height="100%"
                    cursorStart={1}
                    onLoad={this.onLoad}
                    onChange={this.onChange}
                    fontSize={this.state.fontSize}
                    showPrintMargin={this.state.showPrintMargin}
                    showGutter={this.state.showGutter}
                    highlightActiveLine={this.state.highlightActiveLine}
                    focus
                    value={this.state.value}
                />
            </Flexbox>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/page/process/ProcessTextEditor.js